// The home page's header for the customer pages (/buy-cake, /cart, /orders):
// same look, with the cake menu, their account and the cart.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiMenu, FiShoppingCart, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import {
  getAccountPath,
  getLoggedInName,
  isLoggedIn,
  isStaffUser,
} from "utils/auth";
import { cartCount, useCart } from "utils/cart";
import logo from "assets/the-oven-delights-logo.png";
import LanguageSwitcher, { LanguageOptions } from "components/LanguageSwitcher";
import "screens/Home/styles.css";

// Sizes in px: Bootstrap's !important .px-4/.py-2 would override Tailwind's.
const HEADER_BUTTON =
  "h-[38px] px-[18px] rounded-full bg-pink-500 hover:bg-pink-600 text-white text-sm font-semibold shadow transition-colors no-underline inline-flex items-center justify-center gap-2 border-0 whitespace-nowrap";
const NAV_LINK =
  "text-pink-100 hover:text-pink-600 transition-colors no-underline";
const GLASS_LINK =
  "px-[14px] py-[10px] text-white/90 font-semibold no-underline hover:text-white hover:bg-white/10 rounded-xl transition-colors";

export default function StoreHeader({ onLogout }) {
  const { t } = useTranslation("home");
  const { t: ts } = useTranslation("shop");
  const count = cartCount(useCart());
  const [open, setOpen] = useState(false);
  const [menuTop, setMenuTop] = useState(70);
  const headerRef = useRef(null);
  const menuRef = useRef(null);
  const close = () => setOpen(false);

  // Close the mobile menu on a tap/click outside it, or on Escape.
  useEffect(() => {
    if (!open) return undefined;
    if (headerRef.current) {
      setMenuTop(Math.floor(headerRef.current.getBoundingClientRect().bottom));
    }
    const closeOnOutside = (e) => {
      const inside = [headerRef, menuRef].some(
        (r) => r.current && r.current.contains(e.target),
      );
      if (!inside) setOpen(false);
    };
    const closeOnEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("touchstart", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("touchstart", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const account = isLoggedIn() && {
    to: getAccountPath(),
    label: isStaffUser() ? t("nav.dashboard") : t("nav.myAccount"),
  };
  const logout = () => {
    close();
    if (onLogout) onLogout();
  };
  const cartLabel = (
    <>
      <FiShoppingCart />
      <span>{ts("nav.cart")}</span>
      {count > 0 && (
        <span className="bg-white text-pink-600 px-[8px] py-[1px] rounded-full text-xs font-bold">
          {count}
        </span>
      )}
    </>
  );

  return (
    <>
      <header
        ref={headerRef}
        className="w-full bg-white/90 backdrop-blur-md sticky top-0 z-40 shadow-sm font-sans"
      >
        <div className="w-full md:max-w-6xl md:mx-auto px-2 md:px-6 py-2 md:py-4 flex items-center justify-between">
          <Link
            to="/home"
            className="flex items-center gap-2 md:gap-3 no-underline"
          >
            <div className="w-9 h-9 md:w-12 md:h-12 bg-gradient-to-br from-pink-300 to-yellow-300 rounded-full flex items-center justify-center shadow-lg shrink-0">
              <img
                src={logo}
                alt="logo"
                className="w-9 h-9 md:w-12 md:h-12 rounded-full"
              />
            </div>
            <div>
              <h1 className="text-base md:text-xl font-extrabold text-pink-600 leading-tight m-0">
                The Oven Delights
              </h1>
              <p className="font-sans text-xs text-pink-300 hidden sm:block m-0">
                {isLoggedIn() && getLoggedInName()
                  ? t("nav.welcome", { name: getLoggedInName() })
                  : t("nav.tagline")}
              </p>
            </div>
          </Link>
          <div className="flex items-center gap-[8px] md:gap-[24px]">
            <nav className="hidden md:flex gap-6 items-center text-sm font-semibold">
              <Link to="/buy-cake" className={NAV_LINK}>
                {t("nav.menu")}
              </Link>
              {account && (
                <Link to={account.to} className={HEADER_BUTTON}>
                  {account.label}
                </Link>
              )}
              <Link to="/cart" className={HEADER_BUTTON}>
                {cartLabel}
              </Link>
              {account && onLogout && (
                <Link to="/login" onClick={logout} className={NAV_LINK}>
                  {ts("nav.logout")}
                </Link>
              )}
            </nav>
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>
            {/* On phones the cart stays in view, beside the menu button */}
            <Link
              to="/cart"
              aria-label={ts("nav.cart")}
              className="md:hidden relative w-11 h-11 flex items-center justify-center rounded-2xl bg-white/15 backdrop-blur-md border border-white/30 text-white text-xl shadow-lg no-underline"
            >
              <FiShoppingCart />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-[4px] rounded-full bg-pink-500 text-white text-[11px] font-bold flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            <button
              type="button"
              onClick={() => setOpen(!open)}
              aria-label={open ? t("stories.close") : t("nav.menu")}
              aria-expanded={open}
              className="md:hidden w-11 h-11 flex items-center justify-center rounded-2xl bg-white/15 backdrop-blur-md border border-white/30 text-white text-2xl shadow-lg hover:bg-white/25 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              {open ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>
      </header>
      {open && (
        <div
          ref={menuRef}
          style={{ top: menuTop }}
          className="font-sans md:hidden fixed inset-x-0 z-40 bg-[#1b1a2e]/80 backdrop-blur-xl border-t border-white/10 rounded-b-[28px] shadow-2xl overflow-hidden"
        >
          <nav className="flex flex-col gap-[4px] px-[16px] pt-[12px] pb-[8px]">
            <Link to="/buy-cake" onClick={close} className={GLASS_LINK}>
              {t("nav.menu")}
            </Link>
            {account && (
              <Link to={account.to} onClick={close} className={GLASS_LINK}>
                {account.label}
              </Link>
            )}
            {account && onLogout && (
              <Link to="/login" onClick={logout} className={GLASS_LINK}>
                {ts("nav.logout")}
              </Link>
            )}
          </nav>
          <div className="border-t border-white/10">
            <LanguageOptions glass />
          </div>
        </div>
      )}
    </>
  );
}
